import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import { divyaDesams } from '../data/divyaDesams';
import './DivyaDesamDetail.css';

const DivyaDesamDetail = () => {
  const { slug } = useParams();
  const hydrated = useHydrated();

  const temple = divyaDesams.find(t => t.slug === slug);

  if (!temple) {
    return (
      <div className="desam-detail-page">
        <div className="container">
          <h1>Temple not found</h1>
          <p>The Divya Desam temple you are looking for does not exist or the URL may be incorrect.</p>
          <Link to="/108-divya-desams" className="btn btn-primary">Back to 108 Divya Desams</Link>
        </div>
      </div>
    );
  }

  const nearbyTemples = divyaDesams
    .filter(t => t.nadu === temple.nadu && t.id !== temple.id)
    .slice(0, 4);

  return (
    <div className="desam-detail-page">
      <SEO
        title={`${temple.name} - Divya Desam #${temple.id} | ${temple.city}, ${temple.state}`}
        description={`${temple.brief} Visit ${temple.name} dedicated to ${temple.deity} in ${temple.city}, ${temple.district}. One of the 108 Divya Desams in ${temple.nadu}.`}
        keywords={`${temple.name}, Divya Desam, ${temple.deity}, ${temple.city}, ${temple.nadu}, ${temple.district}, 108 Divya Desams, Vishnu temple, ${temple.goddess}`}
        canonicalPath={`/108-divya-desams/${temple.slug}`}
        ogType="article"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "HinduTemple",
          "name": temple.name,
          "description": temple.brief,
          "image": temple.image,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": temple.city,
            "addressRegion": temple.state,
            "addressCountry": "IN"
          },
          "geo": {
            "@type": "GeoCoordinates"
          },
          "isPartOf": {
            "@type": "ItemList",
            "name": "108 Divya Desams",
            "numberOfItems": 108
          }
        }]}
      />

      <div className="container">
        {/* Breadcrumb */}
        <nav className="desam-detail-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span className="desam-detail-breadcrumb-sep">&gt;</span>
          <Link to="/108-divya-desams">108 Divya Desams</Link>
          <span className="desam-detail-breadcrumb-sep">&gt;</span>
          <span className="desam-detail-breadcrumb-current">{temple.name}</span>
        </nav>

        {/* Hero Section */}
        <motion.div
          className="desam-detail-hero"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="desam-detail-hero-image">
            <img src={temple.image} alt={temple.name} loading="eager" />
            <div className="desam-detail-hero-overlay">
              <span className="desam-detail-hero-badge">#{temple.id} of 108</span>
            </div>
          </div>
          <div className="desam-detail-hero-info">
            <h1 className="desam-detail-hero-title">{temple.name}</h1>
            <p className="desam-detail-hero-subtitle">Divya Desam #{temple.id} of 108</p>
          </div>
        </motion.div>

        {/* Quick Info Bar */}
        <motion.div
          className="desam-detail-quick-info"
          initial={hydrated ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <div className="desam-detail-info-item">
            <span className="desam-detail-info-label">Nadu</span>
            <span className="desam-detail-info-value desam-detail-nadu-badge">{temple.nadu}</span>
          </div>
          <div className="desam-detail-info-item">
            <span className="desam-detail-info-label">City</span>
            <span className="desam-detail-info-value">{temple.city}</span>
          </div>
          <div className="desam-detail-info-item">
            <span className="desam-detail-info-label">State</span>
            <span className="desam-detail-info-value">{temple.state}</span>
          </div>
          <div className="desam-detail-info-item">
            <span className="desam-detail-info-label">Deity</span>
            <span className="desam-detail-info-value">{temple.deity}</span>
          </div>
          <div className="desam-detail-info-item">
            <span className="desam-detail-info-label">Goddess</span>
            <span className="desam-detail-info-value">{temple.goddess}</span>
          </div>
        </motion.div>

        {/* Description Section */}
        <motion.section
          className="desam-detail-section"
          initial={hydrated ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <h2 className="desam-detail-section-title">About {temple.name}</h2>
          <div className="desam-detail-description">
            <p>{temple.description}</p>
          </div>
        </motion.section>

        {/* Significance Section */}
        {temple.significance && temple.significance.length > 0 && (
          <motion.section
            className="desam-detail-section"
            initial={hydrated ? { opacity: 0, y: 20 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <h2 className="desam-detail-section-title">Significance</h2>
            <ul className="desam-detail-significance-list">
              {temple.significance.map((item, index) => (
                <li key={index} className="desam-detail-significance-item">{item}</li>
              ))}
            </ul>
          </motion.section>
        )}

        {/* Festivals Section */}
        {temple.festivals && temple.festivals.length > 0 && (
          <motion.section
            className="desam-detail-section"
            initial={hydrated ? { opacity: 0, y: 20 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
          >
            <h2 className="desam-detail-section-title">Festivals</h2>
            <div className="desam-detail-festivals">
              {temple.festivals.map((festival, index) => (
                <span key={index} className="desam-detail-festival-tag">{festival}</span>
              ))}
            </div>
          </motion.section>
        )}

        {/* How to Reach Section */}
        {temple.howToReach && (
          <motion.section
            className="desam-detail-section"
            initial={hydrated ? { opacity: 0, y: 20 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            <h2 className="desam-detail-section-title">How to Reach</h2>
            <p className="desam-detail-how-to-reach">{temple.howToReach}</p>
          </motion.section>
        )}

        {/* Nearby Temples Section */}
        {nearbyTemples.length > 0 && (
          <motion.section
            className="desam-detail-section"
            initial={hydrated ? { opacity: 0, y: 20 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
          >
            <h2 className="desam-detail-section-title">Nearby Temples in {temple.nadu}</h2>
            <div className="desam-detail-nearby-grid">
              {nearbyTemples.map((nearby) => (
                <Link
                  key={nearby.id}
                  to={`/108-divya-desams/${nearby.slug}`}
                  className="desam-detail-nearby-card"
                >
                  <div className="desam-detail-nearby-image">
                    <img src={nearby.image} alt={nearby.name} loading="lazy" />
                    <span className="desam-detail-nearby-badge">#{nearby.id}</span>
                  </div>
                  <div className="desam-detail-nearby-info">
                    <h3 className="desam-detail-nearby-name">{nearby.name}</h3>
                    <p className="desam-detail-nearby-city">{nearby.city}, {nearby.district}</p>
                    <p className="desam-detail-nearby-deity">{nearby.deity}</p>
                  </div>
                </Link>
              ))}
            </div>
          </motion.section>
        )}

        {/* Back Link */}
        <div className="desam-detail-back">
          <Link to="/108-divya-desams" className="desam-detail-back-link">
            &larr; Back to All 108 Divya Desams
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DivyaDesamDetail;
